import PubSub from 'pubsub-js';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { InfoMessage } from '../models';
import { useStores } from '../stores';
import {
  IFRAME_CONTENT_ID,
  MESSAGE_TYPE_RECEIVED,
  MESSAGES_CONTENT_ID,
  PUBSUB_CLEAR_CONVERSATION,
  PUBSUB_LIVE_CHAT_START,
  PUBSUB_SURVEY
} from '../utilities';
import usePrint from './usePrint';

export default function useMenuFunctions() {
  const { i18n } = useTranslation();
  const { app, chat, bot, ui } = useStores();
  const { printElement } = usePrint();

  const getHotTopics = useCallback(() => {
    if (bot.hotTopics.length) {
      chat.addMessage(
        new InfoMessage({
          type: MESSAGE_TYPE_RECEIVED,
          title: 'hotTopics',
          content: bot.hotTopics
        })
      );
    }
  }, [bot.hotTopics, chat]);

  const getTopQuestions = useCallback(() => {
    if (bot.topQuestions.length) {
      chat.addMessage(
        new InfoMessage({
          type: MESSAGE_TYPE_RECEIVED,
          title: 'popularQuestions',
          content: bot.topQuestions
        })
      );
    }
  }, [bot.topQuestions, chat]);

  const printMessages = useCallback(() => {
    printElement(`#${MESSAGES_CONTENT_ID}`, IFRAME_CONTENT_ID);
  }, [printElement]);

  const sendTranscript = useCallback(() => {
    ui.setOpenTranscript(true);
  }, [ui]);

  const deleteConversation = useCallback(() => {
    chat.clearMessages();
    setTimeout(() => {
      PubSub.publish(PUBSUB_CLEAR_CONVERSATION);
    }, 500);
  }, [chat]);

  const changeLanguage = useCallback(
    language => {
      if (i18n.language !== language) {
        if (app.sessionId) {
          chat.updateMessages();
          localStorage.setItem('MesIndexCount', 0);
        }
        bot.topFrequentQuestions = [];
        app.setIsReadyLanguage(false);
        i18n.changeLanguage(language);
        app.updateSessionLanguage(language);
      }
    },
    [app, bot.topFrequentQuestions, chat, i18n]
  );

  const startLiveChat = useCallback(() => {
    // @todo add flag for live chatting
    // for example to disable some buttons
    PubSub.publish(PUBSUB_LIVE_CHAT_START);
  }, []);

  const getFeedback = useCallback(() => {
    PubSub.publish(PUBSUB_SURVEY);
  }, []);

  const functions = useMemo(
    () => ({
      getHotTopics,
      getTopQuestions,
      printMessages,
      sendTranscript,
      deleteConversation,
      changeLanguage,
      startLiveChat,
      getFeedback
    }),
    [
      getHotTopics,
      getTopQuestions,
      printMessages,
      sendTranscript,
      deleteConversation,
      changeLanguage,
      startLiveChat,
      getFeedback
    ]
  );

  const executeFunction = useCallback(
    (name, args) => {
      if (typeof functions[name] === 'undefined') {
        return PubSub.publish(`menu.${name}`);
      }

      return (() => {
        PubSub.publish(`menu.${name}`);
        return functions[name](args);
      })();
    },
    [functions]
  );

  return {
    executeFunction
  };
}
